<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import ClientInfo from '@components/admin/oauth2-client-info'
import { fetchClientList, deleteClient } from '@apis/admin/oauth2'
import { fetchAssets } from '@apis/admin/asset'
import fromPairs from 'lodash/fromPairs'

export default {
  components: { PageNavigation, ClientInfo },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      show_client_info: false,
      target_client_seq: 0,
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadAssets() {
      const { data } = await fetchAssets()
      this.assets = data
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchClientList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search()
    },
    date,
    fromPairs,
    long2ip,
    handleCreateClient() {
      this.show_client_info = true
      this.target_client_seq = null
    },
    handleUpdateClient({ seq }) {
      this.show_client_info = true
      this.target_client_seq = seq
    },
    handleClientSubmit(seq) {
      this.init()
      this.target_client_seq = seq
    },
    async handleDeleteClient(seq) {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.confirm_delete'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await deleteClient(seq)
        await this.loadList()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <div v-loading="loading">
    <ElDialog
      :title="
        target_client_seq
          ? $t('oauth2_client_list.client_information')
          : $t('oauth2_client_list.client_new')
      "
      :visible.sync="show_client_info"
    >
      <ClientInfo
        v-if="show_client_info"
        :key="target_client_seq"
        :client_seq="target_client_seq"
        @submit="handleClientSubmit"
      ></ClientInfo>
    </ElDialog>

    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['client_id', $t('oauth2_client_list.id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td colspan="3">
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('asset') }}</div></th
            >
            <td>
              <select v-model="search_params.asset_seq">
                <option
                  v-for="{ seq, title } in [
                    { seq: undefined, title: $t('admin.total') },
                    ...assets,
                  ]"
                  :key="seq"
                  :value="seq"
                  >{{ title }}</option
                >
              </select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['balance', $t('wallet.balance')],
                    ['holding_balance', $t('wallet.hold_balance')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              &nbsp;
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{
      $t('oauth2_client_list.oauth2_client')
    }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
      <div :class="$style.fr">
        <a
          v-if="$route.params.member_seq"
          href="#"
          :class="[$style.button, $style.green]"
          @click.prevent="handleCreateClient"
          ><span>{{ $t('oauth2_client_list.new') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('oauth2_client_list.name') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('oauth2_client_list.id') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('oauth2_client_list.secret') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('oauth2_client_list.registration_date') }}</div></th
          >
          <th scope="col"><div>&nbsp;</div></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(data, idx) in list"
          :key="data.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
          <template v-if="!$route.params.member_seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data.user_id }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.user_name
            }}</td>
          </template>
          <td :class="$style.wide">{{ data.title }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{ data.client_id }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.client_secret
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
          <td :class="[$style.tRight, $style.nowrap]">
            <a
              href="#"
              :class="[$style.button]"
              @click.prevent="handleUpdateClient(data)"
              ><span>{{ $t('admin.modify') }}</span></a
            >
            {{ ' ' }}
            <a
              href="#"
              :class="[$style.button, $style.red]"
              @click.prevent="handleDeleteClient(data.seq)"
              ><span>{{ $t('admin.delete') }}</span></a
            >
          </td>
        </tr>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 6 : 8"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 6 : 8"
            :class="$style.tCenter"
            >{{ $t('oauth2_client_list.msg_no_clints') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
